import { ContentRestrictionCriteria } from "../../../api/types/NotificationDataTypes.js";
import { UserRole } from "../../../login/UserRole.js";
import { getAuthProviderButtonsToShow } from "../../../gui/utils.js";
import { ClientSdkPublicInterface, isDiscount, isFreeTrial, isMembershipRequired, isMonthSub, isYearSub, MailFixDialogOutcome, MembershipDialogFlag, MembershipFlagsBasicConf } from "@api-commons/sdk/ClientSdkPublicInterface.js";
import { FollowHTMLElement } from "@api-commons/sdk/modules/FollowParseAndAttachModule.js";
import { BrowserLogger, Logger } from "@ciaopeople/cp-shatsu/Logging";
import { UserActionIdentifier } from "../../../api/commondata/UserActionError.js";
import { CurrentSessionProxy } from "../../../login/CurrentSessionProxy.js";
import { ApplicationUrlHandler } from "../../modules/ApplicationUrlHandler.js";
import { SdkHandledHTMLElement, SubscriptionCallbackData } from "../../modules/SdkHandledHTMLElement.js";
import { AAABarrierWithSource, BarrierDisplayMode, LoginExtent, TrackingAndLoginOptions } from "../AAABarrierWithSource.js";
import { NewsletterSubscriptionHTMLElement } from "@api-commons/sdk/modules/NewsletterParseAndAttachModule.js";
import { validMailCheck, validNameCheck } from "@api-commons/utils/MailVaildation.js";
import { inEnum, isObject } from "@ciaopeople/cp-shatsu/TypeChecking";
import { CTA_NAME, CTA_OUTCOME } from "@api-commons/stats/CTATypes.js";
import { UserBasicInfo } from "@api-commons/api/types/UserDataTypes.js";
import { use } from "chai";
import { isLoggedInSession } from "@api-commons/session/StorableSession.js";






function extentHasNoRequirements(extent: LoginExtent): boolean {
    return extent == LoginExtent.REGISTER_USER;
}

function exentHasRequirements(extent: LoginExtent): boolean {
    return !extentHasNoRequirements(extent);
}

function extentRequiresEmail(extent: LoginExtent): boolean  {
    return extent != LoginExtent.REGISTER_USER;
}

function extentRequiresName(extent: LoginExtent): boolean {
    return extent == LoginExtent.REGISTER_USER_WITH_VALID_EMAIL_AND_PERSONAL_DATA ||
            extent  == LoginExtent.REGISTER_USER_WITH_VALID_EMAIL_AND_PERSONAL_DATA_2STEPS
}


function extentRequiresTwoSteps(extent: LoginExtent): boolean {
    return extent == LoginExtent.REGISTER_USER_WITH_VALID_EMAIL_2STEPS ||
            extent  == LoginExtent.REGISTER_USER_WITH_VALID_EMAIL_AND_PERSONAL_DATA_2STEPS
}


function validateUserBasicAgainstExtent(user: UserBasicInfo, extent: LoginExtent): boolean {
    let retval = false;
    if(extentHasNoRequirements(extent) && user && user.id) {
        retval = true;
    }
    else {
        if(extentRequiresEmail(extent) && user && user.email && validMailCheck(user.email)) {
            retval = true;
        }
        if(extentRequiresName(extent)){
            retval = user && user.nameAndGender && validNameCheck(user.nameAndGender.first_name,user.nameAndGender.last_name);
        } 
    }
    return retval;
}

function nextStepExtent(extent: LoginExtent): LoginExtent {
    return LoginExtent.REGISTER_USER_WITH_VALID_EMAIL_AND_PERSONAL_DATA_2STEPS?
                LoginExtent.REGISTER_USER_WITH_VALID_EMAIL_AND_PERSONAL_DATA:
                LoginExtent.REGISTER_USER_WITH_VALID_EMAIL;
}



function actionTagToSourceElement(act: UserActionIdentifier): string {
    if(act == UserActionIdentifier.FOLLOW) return "follow";
    else if (act == UserActionIdentifier.NEWSLETTER_SUBSCRIBE) return "newsletter_subscribe";
    else if (act == UserActionIdentifier.BOOKMARK) return "bookmark";
    else return "unspecified";
}

export class LoggedInBarrier<Barricaded> implements AAABarrierWithSource<SdkHandledHTMLElement<any>, Barricaded> {
    protected logger: Logger;

    constructor(protected readonly sdk: ClientSdkPublicInterface,
                protected barricaded: Barricaded,
                protected urlHandler: ApplicationUrlHandler,
                protected currentSessionProxy: CurrentSessionProxy,
                protected barrierDisplayMode: BarrierDisplayMode = BarrierDisplayMode.OPEN_URL
            ) {
        this.logger = new BrowserLogger("[LoginBarrier]", "#f00");
    }

    executeWithBarrierWithSource<A>(dom: SdkHandledHTMLElement<any>, func: (barr: Barricaded) => Promise<A>): Promise<A|void> {
        let data: any;
        let sourceIntent: "newsletter" | "follow" | "other" = "other";
        let ourl: string = null;
        const topic_label = dom.getTopicLabel();
        let dialog_title: string = null;
        // ? Non mi piace, è un mezzo workaround. Però per il momento è più che sufficiente
        if (dom instanceof FollowHTMLElement) {
            this.logger.log("Is FollowHTMLElement", dom.getUrl());
            data = dom.getUrl();
            sourceIntent = "follow";
            ourl = data;
            if(dom.getContentRestrictionCriteria() == ContentRestrictionCriteria.RESTRICT_TO_MEMBERS){
                dialog_title = "Abbonati ed ascolta il podcast"
            }
            
        }
        else if (dom instanceof NewsletterSubscriptionHTMLElement) {
            sourceIntent = "newsletter"
            ourl = dom.getUrl();
        }

        

        const extent = dom instanceof NewsletterSubscriptionHTMLElement?LoginExtent.REGISTER_USER_WITH_VALID_EMAIL:LoginExtent.REGISTER_USER;

        
        return this.loginSessionInternalExecute(
            { 
                utm_medium: dom.getUTMmedium(), 
                utm_source:  dom.getUTMSource(),
                redirect_url: dom.getRedirectUrl()?dom.getRedirectUrl():ourl,
                topic_label: topic_label,
                cta_topic: topic_label,
                dialog_title: dialog_title
            },
            dom.getAssociatedUserAction(),
            dom.getContentRestrictionCriteria() == ContentRestrictionCriteria.RESTRICT_TO_MEMBERS?MembershipFlagsBasicConf:null,
            extent,
            func,
            dom.hasExecuteAfterSubscriptionCallback()?(d) => dom.executeAfterSubscriptionCallback(d):null,
            data
        );
    }

    

    
    protected async loginSessionInternalExecute<A>(
        extra_data: TrackingAndLoginOptions, 
        actionTag: UserActionIdentifier, 
        membershipFlags: MembershipDialogFlag [],
        extent: LoginExtent,
        func: (barr: Barricaded) => Promise<A>, 
        afterSignUpCallback: (d: SubscriptionCallbackData) => void,
        data?: any,
        overrideBarrierDisplayMode?: BarrierDisplayMode
    ): Promise<A|void> {
        const session = this.currentSessionProxy.getCurrentSession();
        const isLoggedIn = session && session.role == UserRole.AUTHENTICATED_USER;
        const membershipEnabled = this.sdk.getConfig().membership?.enable;
        
        const useDisplayMode: BarrierDisplayMode = overrideBarrierDisplayMode?overrideBarrierDisplayMode:this.barrierDisplayMode;

        const source_el = extra_data?.utm_source?extra_data.utm_source:actionTagToSourceElement(actionTag);
        if(isMembershipRequired(membershipFlags)) {
            if(membershipEnabled) {

                const openBuyNew = async () => {
                    if(useDisplayMode == BarrierDisplayMode.OPEN_URL) {
                        if(!isYearSub(membershipFlags) && isMonthSub(membershipFlags)) {
                            this.sdk.handleBuyPersonalMembershipMonth({
                                returnUrl: extra_data?.redirect_url,
                                useDiscount: isDiscount(membershipFlags),
                                useFreeTrial: isFreeTrial(membershipFlags)
                            });
                        }
                        else {
                            this.sdk.handleBuyPersonalMembershipYear({
                                returnUrl: extra_data?.redirect_url,
                                useDiscount: isDiscount(membershipFlags),
                                useFreeTrial: isFreeTrial(membershipFlags)
                            });
                        }
                    }
                    else {
                        return this.sdk.showBuyDialog(extra_data?.redirect_url,membershipFlags,
                                {
                                        title: extra_data?.dialog_title,
                                        subtitle: extra_data?.dialog_subtitle,
                                        cta: {
                                            action: CTA_NAME.MEMBERSHIP_REQUIRED_DIALOG,
                                            outcome: CTA_OUTCOME.SHOWN,
                                            source_element: source_el
                                        }
                                }
                        );
                    } 

                };      

                const userIsMember = await this.sdk.isMember()
                if(userIsMember) func(this.barricaded);
                else {
                    const loginResult = await openBuyNew();
                    if(loginResult && loginResult.session && isLoggedInSession(loginResult.session)) {
                        const isNowMember = await this.sdk.isMember();
                        if(isNowMember) return func(this.barricaded);
                    }
                
                }
                
            }
            else {
                this.logger.log("Element requires membership but membership is not enabled", data);
                return Promise.reject("Cannot execute action");
            }
        }
        else if(isLoggedIn) {
            if(exentHasRequirements(extent)) {
                const rr = await this.sdk.getMe(true);
                if(validateUserBasicAgainstExtent(rr.data,extent)) {
                    return func(this.barricaded);
                }
                else {
                    const source_el = extra_data?.utm_source?extra_data.utm_source:actionTagToSourceElement(actionTag);
                    const r = await this.sdk.showMailFixOrLoginDialog(true,
                            actionTag,
                            afterSignUpCallback,
                            extra_data.redirect_url,
                            extra_data.topic_label,
                            {
                                cta: {
                                    action: CTA_NAME.EMAIL_FIX_REQUIRED_DIALOG,
                                    outcome: CTA_OUTCOME.SHOWN,
                                    source_element: source_el
                                }
                            }
                        );
                    if(r == MailFixDialogOutcome.VERIFICATION_COMPLETED_VIA_OTP) return func(this.barricaded);
                    else return;
                        
                }
            }       
            else {
                return func(this.barricaded);
            }
        }
        else {

            this.logger.log("User not logged, prompting login", extra_data, actionTag, data);
            const openLogin = async () => {
                if(useDisplayMode == BarrierDisplayMode.OPEN_URL) {
                    this.sdk.openSettingsUrl("login", extra_data?.utm_source, extra_data?.utm_medium, {
                            action: CTA_NAME.LOGIN_REQUIRED_DIALOG,
                            url: data
                    });
                    return null;
                }
                else {
                    const _prov = getAuthProviderButtonsToShow(this.sdk, false);
                    const maybeSession = await this.sdk.showLoginDialog(extra_data?.redirect_url, {
                            title: extra_data?.dialog_title, 
                            subtitle: extra_data?.dialog_subtitle,
                            cta: {
                                action: CTA_NAME.LOGIN_REQUIRED_DIALOG,
                                outcome: CTA_OUTCOME.SHOWN,
                                source_element: source_el
                            }
                        });
                    if(maybeSession && isLoggedInSession(maybeSession.session)) {
                        if(exentHasRequirements(extent)) {
                            const rr = await this.sdk.getMe(true);
                            if(validateUserBasicAgainstExtent(rr.data,extent)) {
                                return func(this.barricaded);
                            }
                            else {
                               this.loginSessionInternalExecute(
                                                extra_data,
                                                actionTag,
                                                membershipFlags,
                                                nextStepExtent(extent),
                                                func,
                                                afterSignUpCallback,
                                                data
                                            );
                            }
                            
                        }
                        else {
                            return func(this.barricaded);
                        }
                    }

                }

            }

            // @task #13579 - plain open dialog for everyone
            if(extent == LoginExtent.REGISTER_USER_WITH_VALID_EMAIL) {
                return new Promise(async (resolve,reject) => {
                   
                    const outcome = await this.sdk.showMailFixOrLoginDialog(false,
                        actionTag,
                        afterSignUpCallback,
                        extra_data.redirect_url,
                        extra_data.topic_label,
                        {
                            cta: {
                                action: CTA_NAME.EMAIL_LOGIN_REQUIRED_DIALOG,
                                outcome: CTA_OUTCOME.SHOWN,
                                source_element: source_el
                            }
                        }
                    )
                    if(outcome == MailFixDialogOutcome.LOGIN_COMPLETED_VIA_OTP ||
                          outcome == MailFixDialogOutcome.VERIFICATION_COMPLETED_VIA_OTP
                    ) {
                        await func(this.barricaded);
                    }                   
                    else resolve();
                    
                })
            }
            else {
                return openLogin();
            }

                
            
        }
    }


    executeWithBarrier<A>(func: (barr: Barricaded) => Promise<A>): Promise<A|void> {
        return this.loginSessionInternalExecute(null, UserActionIdentifier.GENERIC_CALL, null, LoginExtent.REGISTER_USER, func,null);
    }

    executeWithBarrierAndOptions<A>(func: (barr: Barricaded) => Promise<A>, options?: TrackingAndLoginOptions, membershipFlags?: MembershipDialogFlag[], loginExtent?: LoginExtent, overrideDisplayMode?: BarrierDisplayMode): Promise<A|void> {
        return this.loginSessionInternalExecute(options, UserActionIdentifier.GENERIC_CALL, membershipFlags, 
            loginExtent?loginExtent:LoginExtent.REGISTER_USER, func,null, null, overrideDisplayMode);
    }

    getBarricadedService(): Barricaded {
        return this.barricaded;
    }



}
    